import { UserGroupIcon } from "@heroicons/react/24/outline";
import FlagOutlineIcon from "../shared-components/icons/FlagOutlineIcon";
import { ItemWithMetaData } from "../api/Items";

import { classNames, formattedDate, atLeastOneItem } from "../utils";
import KeywordBadges from "../shared-components/KeywordBadges";
import { ReportSummaryItemApi } from "../api/Reports";
import ButtonAffiliationEdit from "./ButtonAffiliationEdit";
import ButtonAffiliationRemove from "./ButtonAffiliationRemove";
import RatingBadges from "../shared-components/RatingBadges";
import { itemToAffiliation } from "./helpers";

type Props = {
  item: ItemWithMetaData;
  report: ReportSummaryItemApi;
  isReadOnly?: boolean;
  isAmendment?: boolean;
  handleAction: (
    action: "edit" | "remove" | "add" | "cancel" | "completed" | "selected",
    values?: string[] | boolean
  ) => void;
  behaviors?: string[];
  hide?: boolean;
};

const AffiliationItem = ({
  item,
  report,
  isReadOnly,
  isAmendment,
  handleAction,
  behaviors,
  hide,
}: Props) => {
  const affiliation = itemToAffiliation(item);

  const flags = item.rating.filter(
    (rating) => rating.score_type === "behavior"
  );

  return (
    <div
      className={classNames(
        "flex border border-gray-300 rounded-md p-4 my-4",
        hide ? "hidden" : ""
      )}
    >
      <div className="flex-1 min-w-0">
        <div>
          <div className="flex items-center">
            <div className="w-full flex items-center">
              <div className="w-6 p-1 rounded-full bg-sky-800 text-white mr-3">
                <UserGroupIcon className="w-4"></UserGroupIcon>
              </div>
              <div className="max-w-lg truncate flex-col">
                <div className="flex items-center">
                  <div className="text-sm">{affiliation.affiliation_type}</div>
                  <div>
                    <a
                      className="self-start text-blue-500 text-sm ml-2 font-thin"
                      href={affiliation.url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      ( View Link )
                    </a>
                  </div>
                </div>
                {affiliation.created_at && (
                  <div className="text-xs font-light text-gray-500">
                    {formattedDate(affiliation.created_at)}
                  </div>
                )}
              </div>

              {!isReadOnly && (
                <div className="ml-auto flex gap-3">
                  <ButtonAffiliationRemove
                    handleAction={handleAction}
                    item={affiliation}
                  />
                  {behaviors && (
                    <ButtonAffiliationEdit
                      handleAction={handleAction}
                      item={affiliation}
                      report={report}
                      behaviors={behaviors}
                      isAmendment={isAmendment}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex border-t border-1 border-gray-300 mt-4 gap-4 py-3">
          <div className="flex items-center gap-1 text-sm border border-gray-400 rounded-md px-2 py-1">
            <span>Source:</span>
            <span className="font-light text-gray-500">
              {affiliation.affiliation_source}
            </span>
          </div>

          <div className="flex items-center gap-1 text-sm border border-gray-400 rounded-md px-2 py-1">
            <span>Activity:</span>
            <span className="font-light text-gray-500">
              {affiliation.affiliation_activity}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-500 mb-3 text-justify break-words py-2">
          {affiliation.text}
        </div>

        {(flags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-3">
            <FlagOutlineIcon />
            <RatingBadges ratings={flags} />
          </div>
        )}
        {item.keywords && atLeastOneItem(item.keywords) && (
          <KeywordBadges keywords={item.keywords} />
        )}
        {affiliation.media_urls && affiliation.media_urls.length > 0 && (
          <>
            <div className="max-w-[1000px] mx-auto grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5  gap-4 mt-4">
              {affiliation.media_urls.map((imageSrc) => (
                <div
                  className="flex items-center justify-center relative"
                  key={imageSrc}
                >
                  <img
                    src={imageSrc}
                    alt="Pasted"
                    className="max-h-48 max-w-48 rounded-md"
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AffiliationItem;
